import React from "react";
import { inr, num, pct, calcLive } from "../lib/api";
import { Button } from "../components/ui/button";
import { Pencil, Trash2, TrendingUp, TrendingDown, BookOpen, Sparkles } from "lucide-react";
import { Badge } from "../components/ui/badge";

export default function TradesTable({ trades, prices, onEdit, onDelete, onJournal }) {
  if (!trades || trades.length === 0) {
    return (
      <div data-testid="empty-trades" className="bg-[#121318] border border-[#27272A] p-16 text-center">
        <div className="text-xs tracking-[0.3em] uppercase text-[#71717A]">No Trades Yet</div>
        <div className="mt-3 text-[#A1A1AA] text-sm">Add your first trade to start tracking.</div>
      </div>
    );
  }

  return (
    <div className="bg-[#121318] border border-[#27272A] overflow-hidden" data-testid="trades-table-wrapper">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#08080A] border-b border-[#27272A] sticky top-0 z-10">
            <tr className="text-[10px] tracking-[0.2em] uppercase text-[#71717A]">
              <th className="text-left font-semibold px-3 py-3">Stock</th>
              <th className="text-left font-semibold px-3 py-3 hidden md:table-cell">Broker</th>
              <th className="text-left font-semibold px-3 py-3 hidden lg:table-cell">Cat.</th>
              <th className="text-left font-semibold px-3 py-3">Side</th>
              <th className="text-right font-semibold px-3 py-3">Rate</th>
              <th className="text-right font-semibold px-3 py-3 hidden sm:table-cell">Qty</th>
              <th className="text-right font-semibold px-3 py-3 hidden md:table-cell">Invested</th>
              <th className="text-right font-semibold px-3 py-3">LTP</th>
              <th className="text-right font-semibold px-3 py-3">Chg %</th>
              <th className="text-right font-semibold px-3 py-3 hidden lg:table-cell">Exit Rate</th>
              <th className="text-right font-semibold px-3 py-3 hidden lg:table-cell">Risk %</th>
              <th className="text-right font-semibold px-3 py-3">P&amp;L</th>
              <th className="text-left font-semibold px-3 py-3">Status</th>
              <th className="text-right font-semibold px-3 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t, idx) => {
              const priceData = prices?.[t.stock?.toUpperCase()];
              const ltp = priceData?.last;
              const isClosed = t.status === "CLOSED";
              // For closed trades, use exit_rate as effective price
              const effectivePrice = isClosed ? t.exit_rate : ltp;
              const live = calcLive(t, effectivePrice);
              const plColor = live.livePL == null ? "text-[#A1A1AA]" : live.livePL >= 0 ? "text-[#00C805]" : "text-[#FF3B30]";
              const chgColor = live.changePct == null ? "text-[#A1A1AA]" : live.changePct >= 0 ? "text-[#00C805]" : "text-[#FF3B30]";
              return (
                <tr
                  key={t.id}
                  data-testid={`trade-row-${t.id}`}
                  className={`border-b border-[#27272A]/60 hover:bg-[#1A1B23] transition-colors ${idx % 2 === 0 ? "bg-[#121318]" : "bg-[#0F1015]"}`}
                >
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-white tracking-tight">{t.stock}</div>
                    <div className="text-[10px] text-[#71717A] num">{t.exchange === "BO" ? "BSE" : "NSE"} · {t.date_of_purchase}</div>
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-[#A1A1AA] text-xs">{t.broker}</td>
                  <td className="px-3 py-2.5 hidden lg:table-cell text-[#A1A1AA] text-xs">{t.category}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-semibold ${t.transaction === "BOUGHT" ? "text-[#00C805]" : "text-[#FF3B30]"}`}>
                      {t.transaction === "BOUGHT" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {t.transaction === "BOUGHT" ? "BUY" : "SELL"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right num text-white">{num(t.rate)}</td>
                  <td className="px-3 py-2.5 text-right num text-white hidden sm:table-cell">
                    {num(t.quantity, 0)}
                    {t.lots ? <div className="text-[10px] text-[#71717A]" data-testid={`lots-${t.id}`}>{num(t.lots, 0)} lot{t.lots === 1 ? "" : "s"} × {num(t.lot_size, 0)}</div> : null}
                  </td>
                  <td className="px-3 py-2.5 text-right num text-[#A1A1AA] hidden md:table-cell">{inr(t.total_invested)}</td>
                  <td className={`px-3 py-2.5 text-right num ${ltp ? "text-white" : "text-[#71717A]"}`}>
                    {ltp ? num(ltp) : priceData?.error ? <span className="text-[#FFB800] text-xs">N/A</span> : "—"}
                  </td>
                  <td className={`px-3 py-2.5 text-right num text-xs ${chgColor}`}>
                    {live.changePct != null ? pct(live.changePct) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right num text-[#A1A1AA] hidden lg:table-cell">{t.exit_rate ? num(t.exit_rate) : "—"}</td>
                  <td className="px-3 py-2.5 text-right num text-[#FFB800] hidden lg:table-cell text-xs">
                    {live.riskPct != null ? pct(live.riskPct, 1) : "—"}
                  </td>
                  <td className={`px-3 py-2.5 text-right num font-semibold ${plColor}`} data-testid={`pl-${t.id}`}>
                    {live.livePL != null ? inr(live.livePL) : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className={`rounded-sm text-[10px] tracking-[0.15em] uppercase font-medium border ${
                      t.status === "HOLDING" ? "border-[#007AFF]/40 text-[#007AFF] bg-[#007AFF]/10"
                      : t.status === "CLOSED" ? "border-[#71717A]/40 text-[#A1A1AA] bg-[#27272A]/30"
                      : "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10"
                    }`}>{t.status}</Badge>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      <Button data-testid={`journal-${t.id}`} variant="ghost" size="icon" onClick={() => onJournal(t)}
                        className={`h-7 w-7 hover:bg-[#FF9900]/10 rounded-sm ${
                          t.ai_insight || (t.screenshots?.length) || t.emotion
                            ? "text-[#FF9900]" : "text-[#A1A1AA] hover:text-[#FF9900]"
                        }`}
                        title="Trade Journal">
                        {t.ai_insight ? <Sparkles className="h-3.5 w-3.5" /> : <BookOpen className="h-3.5 w-3.5" />}
                      </Button>
                      <Button data-testid={`edit-${t.id}`} variant="ghost" size="icon" onClick={() => onEdit(t)}
                        className="h-7 w-7 text-[#A1A1AA] hover:text-white hover:bg-[#27272A] rounded-sm">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button data-testid={`delete-${t.id}`} variant="ghost" size="icon" onClick={() => onDelete(t)}
                        className="h-7 w-7 text-[#A1A1AA] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
